import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtDateTime, fmtDate } from "../lib/format";
import { Send, Lock, Mail, Phone, CheckCircle2, Circle, Plus, FileText, Download, Paperclip } from "lucide-react";
import ReportPickerModal from "../components/ReportPickerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_TONE = {
  low: "pm-pill-slate", medium: "pm-pill-blue", high: "pm-pill-amber", urgent: "pm-pill-red",
};

export default function CaseDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [kind, setKind] = useState("internal_note");
  const [client, setClient] = useState(null);
  const [showReportPicker, setShowReportPicker] = useState(false);
  const [reportDocs, setReportDocs] = useState([]);

  const load = () => api.get(`/cases/${id}`).then((r) => {
    setData(r.data);
    if (r.data?.case?.client_id) {
      api.get(`/clients/${r.data.case.client_id}`).then((cr) => setClient(cr.data));
    }
  });

  const loadReportDocs = () => api.get(`/cases/${id}/report-documents`).then((r) => setReportDocs(r.data || []));

  const downloadReportDoc = async (rep) => {
    const token = localStorage.getItem("pm_token");
    const res = await fetch(`${API}/reports/${rep.report_id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = rep.filename || "report.pdf";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  useEffect(() => { load(); loadReportDocs(); }, [id]);

  const send = async () => {
    if (!msg.trim()) return;
    await api.post(`/cases/${id}/messages`, { case_id: id, author: "", author_role: "", body: msg, kind });
    setMsg("");
    load();
  };

  const toggleTask = async (t) => {
    await api.patch(`/tasks/${t.id}`, { done: !t.done });
    load();
  };

  const updateStatus = async (status) => {
    await api.patch(`/cases/${id}`, { status });
    load();
  };

  if (!data) return <Layout title="Loading case…" />;

  const { case: c, messages, tasks } = data;

  return (
    <Layout title={c.title} subtitle={`Case · ${c.case_type.replace("_", " ")}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="pm-card p-6" data-testid="case-thread">
            <div className="flex items-center gap-2 mb-4">
              <span className={`pm-pill ${PRIORITY_TONE[c.priority]}`}>{c.priority}</span>
              <span className="pm-pill pm-pill-slate">{c.case_type.replace("_", " ")}</span>
              <div className="ml-auto flex items-center gap-1">
                {["open", "pending", "resolved"].map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(s)}
                    data-testid={`status-btn-${s}`}
                    className={`px-3 h-8 rounded-lg text-xs font-bold ${
                      c.status === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {messages.map((m) => (
                <div key={m.id} className={`rounded-xl p-4 ${m.kind === "internal_note" ? "bg-amber-50 border border-amber-200" : "bg-slate-50 border border-slate-200"}`}
                     data-testid={`case-message-${m.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-slate-900">{m.author}</span>
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{m.author_role}</span>
                      <span className={`pm-pill ${m.kind === "internal_note" ? "pm-pill-amber" : m.kind === "call_log" ? "pm-pill-blue" : "pm-pill-slate"}`}>
                        {m.kind === "internal_note" ? <><Lock size={10} /> internal</> : m.kind === "call_log" ? <><Phone size={10} /> call</> : <><Mail size={10} /> external</>}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 font-mono-tab">{fmtDateTime(m.created_at)}</div>
                  </div>
                  <div className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">{m.body}</div>
                </div>
              ))}
              {messages.length === 0 && <div className="text-sm text-slate-500">No messages yet.</div>}
            </div>

            <div className="mt-5 border-t border-slate-200 pt-4">
              <div className="flex items-center gap-2 mb-3">
                {[
                  { id: "internal_note", label: "Internal note", icon: Lock },
                  { id: "external", label: "External email", icon: Mail },
                  { id: "call_log", label: "Call log", icon: Phone },
                ].map((k) => (
                  <button
                    key={k.id}
                    onClick={() => setKind(k.id)}
                    data-testid={`msg-kind-${k.id}`}
                    className={`flex items-center gap-1 px-3 h-8 rounded-lg text-xs font-bold ${
                      kind === k.id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <k.icon size={12} /> {k.label}
                  </button>
                ))}
              </div>
              <textarea
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Write a message…"
                rows={3}
                className="pm-input"
                data-testid="case-message-input"
              />
              <div className="mt-3 flex justify-end">
                <button onClick={send} className="pm-btn-primary h-10 px-4 rounded-xl flex items-center gap-2 text-sm" data-testid="case-message-send">
                  <Send size={14} /> Send
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          {client && (
            <Link to={`/clients/${client.id}`} className="pm-card pm-card-hover p-5 flex items-center gap-3" data-testid="case-client-card">
              <img src={client.photo_url} alt={client.full_name} className="w-12 h-12 rounded-xl object-cover" />
              <div>
                <div className="font-display font-bold text-lg leading-tight tracking-tight">{client.full_name}</div>
                <div className="text-xs text-slate-500">{client.position} · {client.current_club}</div>
              </div>
            </Link>
          )}

          <div className="pm-card p-5" data-testid="case-parties">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Parties</div>
            <div className="flex flex-wrap gap-2">
              {(c.parties || []).map((p, i) => (
                <span key={i} className="pm-pill pm-pill-slate">{p}</span>
              ))}
            </div>
          </div>

          <div className="pm-card p-5" data-testid="case-tasks">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Tasks</div>
              <button className="text-xs font-bold text-emerald-600 inline-flex items-center gap-1" data-testid="add-task-btn">
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="space-y-2">
              {tasks.map((t) => (
                <button key={t.id} onClick={() => toggleTask(t)} className="flex items-start gap-2 w-full text-left hover:bg-slate-50 px-2 py-1.5 rounded-lg"
                        data-testid={`task-${t.id}`}>
                  {t.done ? <CheckCircle2 size={16} className="text-emerald-500 mt-0.5" /> : <Circle size={16} className="text-slate-400 mt-0.5" />}
                  <div className="flex-1">
                    <div className={`text-sm ${t.done ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</div>
                    <div className="text-xs text-slate-400 font-mono-tab">Due {fmtDate(t.due_date)} · {t.assignee}</div>
                  </div>
                </button>
              ))}
              {tasks.length === 0 && <div className="text-sm text-slate-500">No tasks yet.</div>}
            </div>
          </div>

          <div className="pm-card p-5" data-testid="case-documents">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Documents</div>
              <button onClick={() => setShowReportPicker(true)}
                className="text-xs font-bold text-emerald-600 inline-flex items-center gap-1"
                data-testid="case-generate-report-btn">
                <FileText size={12} /> Attach report
              </button>
            </div>
            {reportDocs.length === 0 ? (
              <div className="text-[12px] text-slate-500">No reports attached. Click <b>Attach report</b> to add a generated player profile or portfolio.</div>
            ) : (
              <div className="space-y-1.5">
                {reportDocs.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-slate-200 hover:border-emerald-300"
                       data-testid={`case-doc-${doc.id}`}>
                    <Paperclip size={12} className="text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-bold truncate">{doc.filename || "Report"}</div>
                      <div className="text-[10px] text-slate-500">{doc.added_by_name} · {fmtDateTime(doc.created_at).slice(0, 11)}</div>
                    </div>
                    <button onClick={() => downloadReportDoc(doc)} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid={`case-doc-download-${doc.id}`}>
                      <Download size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {showReportPicker && (
        <ReportPickerModal
          mode="case"
          clientId={c.client_id}
          caseId={id}
          onClose={() => setShowReportPicker(false)}
          onSuccess={() => { setShowReportPicker(false); loadReportDocs(); }}
        />
      )}
    </Layout>
  );
}
